import React, { useState } from 'react';
import { Tree, Dropdown, Button, Input, Modal, Tag } from 'antd';
import {
  FolderOutlined,
  FileOutlined,
  ProfileOutlined,
  PlusOutlined,
  EditOutlined,
  DragOutlined,
  CopyOutlined,
  InboxOutlined,
  DeleteOutlined,
  MoreOutlined,
  StarOutlined,
  ProjectOutlined,
  DatabaseOutlined,
  NodeIndexOutlined,
  NodeCollapseOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import './Sidebar.css';
import DuplicateModal from './DuplicateModal';
import MoveModal from './MoveModal';
import Cookies from 'js-cookie';

export default function SpaceTree({
  space,
  treeData,
  onCreate,
  onMove,
  spaceid,
  onDelete,
  onRename,
  onDuplicate,
  onArchive,
  duplicateModalOpen,
  setDuplicateModalOpen,
  handleDuplicateConfirm,
  selectedDuplicateNode,
  deleteDraftPage,
}) {
  const navigate = useNavigate();

  const [expandedKeys, setExpandedKeys] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const [creatingType, setCreatingType] = useState(null);
  const [creatingParentId, setCreatingParentId] = useState(null);
  const [creatingValue, setCreatingValue] = useState('');

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedMoveNode, setSelectedMoveNode] = useState(null);

  const [hoveredId, setHoveredId] = useState(null);

  const roleId = Number(Cookies.get('space-role-id'));

  const isAdmin = roleId === 1;

  const isViewer = roleId === 5;

  const startCreate = (type, parentId = null) => {
    setCreatingType(type);
    setCreatingParentId(parentId);
    setCreatingValue('');
  };

  const handleCreateSubmit = async () => {
    if (!creatingValue.trim()) return;

    await onCreate(creatingParentId, creatingType, creatingValue);

    setCreatingType(null);
    setCreatingValue('');
  };

  const startRename = (node) => {
    setEditingId(node.item_id);
    setEditingValue(node.title);
  };

  const saveRename = async (node) => {
    await onRename({
      item_id: node.item_id,
      title: editingValue,
      item_type: node.item_type,
    });

    setEditingId(null);
  };

  const cancelRename = () => {
    setEditingId(null);
  };

  const confirmDelete = (node) => {
    const isDraft = node.item_type === 'page' && node.status === 'draft';

    Modal.confirm({
      title: isDraft
        ? `Delete draft "${node.title}"?`
        : `Delete "${node.title}"?`,
      content: isDraft
        ? 'This draft will be permanently deleted.'
        : 'Move item to Trash',
      okText: isDraft ? 'Delete Permanently' : 'Move',
      okType: 'danger',

      onOk: async () => {
        try {
          if (isDraft) {
            await deleteDraftPage(node.item_id);
          } else {
            await onDelete({
              space_id: spaceid,
              item_id: node.item_id,
              item_type: node.item_type,
              status_type: 'deleted',
            });
          }

          navigate(
            `${window.location.origin}/admin/confluence/space/${spaceid}`,
          );
        } catch (error) {
          console.error('Delete failed:', error);
        }
      },
    });
  };

  const handleMoveConfirm = (data) => {
    onMove(data);
  };

  // const createMenu = {
  //   items: [
  //     {
  //       key: 'page',
  //       label: 'Page',
  //       icon: <FileOutlined />,
  //       onClick: () => startCreate('page', null),
  //     },
  //     {
  //       key: 'folder',
  //       label: 'Folder',
  //       icon: <FolderOutlined />,
  //       onClick: () => startCreate('folder', null),
  //     },
  //     {
  //       key: 'whiteboard',
  //       icon: <ProjectOutlined />,
  //       label: 'Whiteboard',
  //       onClick: () => startCreate('whiteboard', null),
  //     },
  //     {
  //       key: 'database',
  //       icon: <DatabaseOutlined />,
  //       label: 'Database',
  //       onClick: () => startCreate('database', null),
  //     },
  //   ],
  // };

  const createMenu = (features , parentId = null) => {
    const items = [];

    if (features?.live_docs === 1) {
      items.push({
        key: 'page',
        label: 'Page',
        icon: <FileOutlined />,
        onClick: () => startCreate('page', parentId),
      });
    }

    if (features?.folders === 1) {
      items.push({
        key: 'folder',
        label: 'Folder',
        icon: <FolderOutlined />,
        onClick: () => startCreate('folder', parentId),
      });
    }

    if (features?.whiteboards === 1) {
      items.push({
        key: 'whiteboard',
        icon: <ProjectOutlined />,
        label: 'Whiteboard',
        onClick: () => startCreate('whiteboard', parentId),
      });
    }

    if (features?.databases_enabled === 1) {
      items.push({
        key: 'database',
        icon: <DatabaseOutlined />,
        label: 'Database',
        onClick: () => startCreate('database', parentId),
      });
    }

    if (features?.smart_links === 1) {
      items.push({
        key: 'smart_links',
        icon: <NodeIndexOutlined />,
        label: 'Smart Link',
      });
    }

    if (features?.blogs === 1) {
      items.push({
        key: 'blogs',
        icon: <NodeCollapseOutlined />,
        label: 'Blogs',
      });
    }

    return { items };
  };

  const getMenu = (node) => ({
    items: [
      {
        key: 'rename',
        label: 'Rename',
        icon: <EditOutlined />,
        onClick: () => startRename(node),
      },
      {
        key: 'move',
        label: 'Move',
        icon: <DragOutlined />,
        onClick: () => {
          setSelectedMoveNode(node);
          setMoveModalOpen(true);
        },
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: <CopyOutlined />,
        onClick: () => onDuplicate(node),
        hidden: node.item_type === 'page' && node.status === 'draft',
      },
      {
        key: 'archive',
        label: 'Archive',
        icon: <InboxOutlined />,
        hidden: node.status === 'draft',
        onClick: () =>
          onArchive({
            item_id: node.item_id,
            item_type: node.item_type,
            status_type: 'archived',
          }),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => confirmDelete(node),
      },
    ],
  });

  return (
    <>
      <div className="space-tree">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            fontWeight: 600,
            color: '#fff',
          }}
        >
          <span>{space.space_name}</span>

          {isAdmin && (
            <Dropdown menu={createMenu(space?.features)} trigger={['click']}>
              <Button
                size="small"
                icon={<PlusOutlined style={{ color: '#fff' }} />}
                type="text"
              />
            </Dropdown>
          )}
        </div>

        <Tree
          treeData={treeData}
          blockNode
          draggable
          defaultExpandAll
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          onSelect={(keys, info) => {
            const node = info.node;

            if (node.item_type === 'page' || node.item_type === 'template') {
              navigate(`space/${Number(spaceid)}/page/${node.item_id}`);
            }

            if (node.item_type === 'whiteboard') {
              navigate(`space/${Number(spaceid)}/whiteboard/${node.item_id}`);
            }

            if (node.item_type === 'database') {
              navigate(`space/${Number(spaceid)}/database/${node.item_id}`);
            }
          }}
          onDrop={(info) => {
            const dragNode = info.dragNode;
            const dropNode = info.node;

            onMove({
              dragId: dragNode.item_id,
              dropId: dropNode.item_id,
            });
          }}
          titleRender={(node) => {
            const isFolder = node.item_type === 'folder';
            const isPublish = node.status === 'published';
            return (
              <div
                className="tree-item"
                onMouseEnter={() => setHoveredId(node.item_id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="tree-left">
                  {node.item_type === 'folder' && (
                    <FolderOutlined style={{ color: '#fff' }} />
                  )}
                  {node.item_type === 'page' && (
                    <FileOutlined style={{ color: '#fff' }} />
                  )}
                  {node.item_type === 'whiteboard' && (
                    <ProjectOutlined style={{ color: '#fff' }} />
                  )}
                  {node.item_type === 'database' && (
                    <DatabaseOutlined style={{ color: '#fff' }} />
                  )}

                  {editingId === node.item_id ? (
                    <Input
                      size="small"
                      autoFocus
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onPressEnter={() => saveRename(node)}
                      onBlur={() => saveRename(node)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') cancelRename();
                      }}
                    />
                  ) : (
                    <div className="tree-title-wrapper">
                      <span className="tree-title">{node.title}</span>

                      {node.item_type === 'page' && node.status === 'draft' && (
                        <Tag className="draft-tag">DRAFT</Tag>
                      )}
                    </div>
                  )}
                </div>

                <div className="tree-actions">
                  {isPublish && isAdmin && hoveredId === node.item_id && (
                    <Dropdown
                      menu={createMenu(space?.features, node.item_id)}
                      trigger={['click']}
                    >
                      <PlusOutlined
                        className="tree-plus"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  )}

                  {isAdmin && (
                    <Dropdown menu={getMenu(node)} trigger={['click']}>
                      <MoreOutlined
                        className="tree-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  )}
                </div>
              </div>
            );
          }}
        />

        {isAdmin && (
          <>
            <div
              style={{
                borderTop: '1px solid rgba(255,255,255,0.15)',
                marginTop: 8,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                color: '#fff',
                opacity: 0.8,
              }}
              onClick={() => navigate(`space/${Number(spaceid)}/starred`)}
            >
              <StarOutlined />
              <span>Starred</span>
            </div>
            <div
              style={{
                borderTop: '1px solid rgba(255,255,255,0.15)',
                marginTop: 8,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                color: '#fff',
                opacity: 0.8,
              }}
              onClick={() => navigate(`space/${Number(spaceid)}/trash`)}
            >
              <DeleteOutlined />
              <span>Trash</span>
            </div>
          </>
        )}
        {creatingType && (
          <div className="tree-create-row">
            {creatingType === 'folder' && <FolderOutlined />}
            {creatingType === 'page' && <FileOutlined />}

            <Input
              size="small"
              autoFocus
              placeholder={`Enter ${creatingType} name`}
              value={creatingValue}
              onChange={(e) => setCreatingValue(e.target.value)}
              onPressEnter={handleCreateSubmit}
            />

            <Button
              size="small"
              type="text"
              className="tree-create-confirm"
              onClick={handleCreateSubmit}
            >
              ✓
            </Button>

            <Button
              size="small"
              type="text"
              className="tree-create-cancel"
              onClick={() => setCreatingType(null)}
            >
              ✕
            </Button>
          </div>
        )}
      </div>
      <DuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        originalNode={selectedDuplicateNode}
        onConfirm={handleDuplicateConfirm}
        spaceid={spaceid}
      />

      <MoveModal
        open={moveModalOpen}
        onClose={() => setMoveModalOpen(false)}
        node={selectedMoveNode}
        onConfirm={handleMoveConfirm}
        spaceid={spaceid}
      />
    </>
  );
}
